// SUB-OFFICE 7 — the five clerk windows, VISUAL layer for this slice:
// frosted glass, the Window-3 tube on its canonical 13s hand-keyed loop,
// stamp arms idling in hard mechanical snaps, and the dot-matrix form print
// effect (stub API — the docket slice calls print()).
//
// STUBBED HOOKS for the gameplay slice:
//   bus 'summons' { windowIdx }        -> buzz + frost brightens (TODO M8)
//   bus 'window:print' { windowIdx, text } -> printForm() animation (TODO M8)
//   bus 'interact' 'window:N'          -> emits 'window:activate' { idx }

import { BoxGeometry, Color, Group, Mesh, MeshBasicMaterial, MeshLambertMaterial, PlaneGeometry } from 'three';
import { PAL, shade } from '../palette';
import { WINDOW_X, WINDOW_Z, W3_TUBE_LOOP, FORM99V_WINDOW } from '../world/layout';
import { makePaperFormTexture } from '../render/textures';
import { patchMaterial } from '../render/scene';
import type { BuiltWorld } from '../render/world';
import type { Entity, EventBus } from './entities';

export class ClerkWindows implements Entity {
  readonly id = 'windows';
  readonly group = new Group();

  private arms: Mesh[] = [];
  private armTimers: number[] = [];
  private armPhase: number[] = []; // 0 idle, 1 raised (snaps back)
  private papers: Mesh[] = [];
  private paperT: number[] = []; // -1 idle; 0..1 printing
  private t = 0;
  private reduced: boolean;
  private w3Mat: MeshBasicMaterial;
  private w3GlowMat: MeshBasicMaterial;
  private gasColor = new Color(PAL.gas).convertSRGBToLinear();
  private summonsLamps: Mesh[] = [];
  private shutters: Mesh[] = [];
  private summoned = -1; // windowIdx (1-based) currently lit, or -1
  private blinkT = 0;
  private pinballT = -1;
  private clerks: Mesh[];
  private clerkBright: number[]; // eased per-clerk brightness (summons lifts one)

  constructor(private bus: EventBus, world: BuiltWorld, reducedMotion: boolean) {
    this.reduced = reducedMotion;
    this.w3Mat = (world.w3Tube.material as MeshBasicMaterial);
    this.w3GlowMat = (world.w3Glow.material as MeshBasicMaterial);
    this.clerks = world.clerkTorsos;
    this.clerkBright = this.clerks.map(() => 1);

    const armGeo = new BoxGeometry(0.5, 0.16, 0.34);
    const armMat = new MeshBasicMaterial({ color: new Color(shade(PAL.carbon, 0.14)) });
    patchMaterial(armMat);
    const paperTex = makePaperFormTexture();
    const paperMat = new MeshBasicMaterial({ map: paperTex, transparent: true });
    patchMaterial(paperMat);

    WINDOW_X.forEach((wx, i) => {
      // stamp arm piston above the counter
      const arm = new Mesh(armGeo, armMat);
      arm.position.set(wx + 0.55, 2.9, WINDOW_Z + 0.42);
      this.group.add(arm);
      this.arms.push(arm);
      this.armTimers.push(4 + Math.random() * 9);
      this.armPhase.push(0);
      // print paper (hidden until a form prints through the slot)
      const paper = new Mesh(new PlaneGeometry(0.62, 0.86), paperMat);
      paper.position.set(wx, 1.2, WINDOW_Z + 0.78);
      paper.rotation.x = -0.5;
      paper.scale.set(1, 0.001, 1);
      paper.visible = false;
      this.group.add(paper);
      this.papers.push(paper);
      this.paperT.push(-1);

      // summons lamp: an amber strip above the window that blinks when lit
      const lampMat = new MeshBasicMaterial({ color: new Color(PAL.amber).convertSRGBToLinear() });
      patchMaterial(lampMat, { dimExempt: true });
      const lamp = new Mesh(new PlaneGeometry(1.7, 0.24), lampMat);
      lamp.position.set(wx, 3.4, WINDOW_Z + 0.32);
      lamp.visible = false;
      this.group.add(lamp);
      this.summonsLamps.push(lamp);

      // purge shutter: carbon plate that arrives all at once
      const shutMat = new MeshBasicMaterial({ color: new Color(shade(PAL.carbon, 0.05)) });
      patchMaterial(shutMat);
      const shutter = new Mesh(new PlaneGeometry(2.1, 1.7), shutMat);
      shutter.position.set(wx, 1.7, WINDOW_Z + 0.34);
      shutter.visible = false;
      this.group.add(shutter);
      this.shutters.push(shutter);
      void i;
    });

    bus.on('interact', (p) => {
      const id = (p as { id: string }).id;
      if (!id.startsWith('window:')) return;
      const idx = parseInt(id.split(':')[1], 10);
      // the docket loop (game.ts) owns the consequences; the window only reports
      this.bus.emit('window:activate', { idx });
      void FORM99V_WINDOW;
    });

    bus.on('window:print', (p) => {
      const { windowIdx } = p as { windowIdx: number };
      this.printForm(windowIdx);
    });

    // summons lighting: one window blinks amber and buzzes until cleared
    bus.on('summons', (p) => {
      const { windowIdx } = p as { windowIdx: number };
      this.summoned = windowIdx;
      this.blinkT = 0;
      this.bus.emit('audio:buzz');
    });
    bus.on('summons:clear', () => {
      if (this.summoned > 0) this.summonsLamps[this.summoned - 1].visible = false;
      this.summoned = -1;
    });

    // purge shutters (mechanisms arrive all at once)
    bus.on('window:shutter', (p) => {
      const { windowIdx } = p as { windowIdx: number };
      if (windowIdx >= 1 && windowIdx <= this.shutters.length) {
        this.shutters[windowIdx - 1].visible = true;
      }
    });
    bus.on('windows:unshutter', () => {
      for (const s of this.shutters) s.visible = false;
    });

    // designation ceremony: five windows light like a pinball bonus
    bus.on('windows:pinball', () => {
      this.pinballT = 0;
    });
  }

  /** Dot-matrix form print: paper grows out of the slot (min 1.2s, masks fetches). */
  printForm(windowIdx: number): void {
    const i = windowIdx - 1;
    if (i < 0 || i >= this.papers.length) return;
    this.paperT[i] = 0;
    this.papers[i].visible = true;
    this.papers[i].scale.y = 0.001;
  }

  update(dt: number): void {
    this.t += dt;

    // clerk paperwork torsos: a subtle shuffling — the stacks lean, settle,
    // and occasionally re-square themselves (still under reduced motion)
    if (!this.reduced) {
      for (let i = 0; i < this.clerks.length; i++) {
        const c = this.clerks[i];
        const ph = this.t * 0.55 + i * 1.71;
        // amplitude fits the shallow lightbox bay: the stacks lean, never clip
        c.rotation.y = Math.sin(ph) * 0.02 + Math.sin(ph * 3.7) * 0.008;
        c.position.y = Math.abs(Math.sin(this.t * 0.9 + i * 2.3)) * 0.014;
      }
    }

    // summoned-clerk brightening: the called window's paperwork lifts ~15%, so
    // the theater of the summons reads even at 1.6m (each torso owns its mat).
    for (let i = 0; i < this.clerks.length; i++) {
      const target = this.summoned === i + 1 ? 1.16 : 1;
      this.clerkBright[i] += (target - this.clerkBright[i]) * Math.min(1, dt * 4);
      (this.clerks[i].material as MeshLambertMaterial).color.setScalar(this.clerkBright[i]);
    }

    // Window-3 tube: the canonical 13s loop (flicker off under reduced motion)
    let intensity = 1;
    if (!this.reduced) {
      const lt = this.t % 13;
      intensity = W3_TUBE_LOOP[W3_TUBE_LOOP.length - 1][1];
      for (let k = 0; k < W3_TUBE_LOOP.length - 1; k++) {
        if (lt >= W3_TUBE_LOOP[k][0] && lt < W3_TUBE_LOOP[k + 1][0]) {
          intensity = W3_TUBE_LOOP[k][1];
          break;
        }
      }
    }
    this.w3Mat.color.copy(this.gasColor).multiplyScalar(0.25 + 0.75 * intensity);
    this.w3GlowMat.opacity = 0.05 + 0.13 * intensity;

    // stamp arms: 3-frame anticipation, then an 80ms slam back — no easing
    for (let i = 0; i < this.arms.length; i++) {
      this.armTimers[i] -= dt;
      const arm = this.arms[i];
      if (this.armPhase[i] === 0 && this.armTimers[i] <= 0) {
        this.armPhase[i] = 1;
        arm.position.y = 3.08; // anticipation: raised, held
        this.armTimers[i] = 0.05 * 3; // three frames of hover
      } else if (this.armPhase[i] === 1 && this.armTimers[i] <= 0) {
        arm.position.y = 2.9; // the slam: arrives all at once
        this.armPhase[i] = 0;
        this.armTimers[i] = 6 + Math.random() * 12;
        this.bus.emit('audio:thunk');
      }
    }

    // form printing: tractor-feed emergence, stepped (printers do not ease)
    for (let i = 0; i < this.papers.length; i++) {
      if (this.paperT[i] < 0) continue;
      this.paperT[i] += dt / 1.4;
      const steps = Math.min(1, Math.floor(this.paperT[i] * 12) / 12);
      this.papers[i].scale.y = Math.max(0.001, steps);
      this.papers[i].position.y = 1.2 + steps * 0.16;
      if (this.paperT[i] >= 1.4) {
        // print complete: the form passes to whoever was summoned (game.ts)
        this.papers[i].visible = false;
        this.paperT[i] = -1;
        this.bus.emit('window:printed', { windowIdx: i + 1 });
      }
    }

    // summons blink: a step function, not a fade — official light arrives whole
    if (this.summoned > 0) {
      this.blinkT += dt;
      const lamp = this.summonsLamps[this.summoned - 1];
      lamp.visible = this.reduced ? true : Math.floor(this.blinkT / 0.4) % 2 === 0;
      if (!this.reduced && this.blinkT > 6 && this.blinkT % 6 < dt) this.bus.emit('audio:buzz');
    }

    // pinball: the five windows light west-to-east, then release
    if (this.pinballT >= 0) {
      this.pinballT += dt;
      for (let i = 0; i < this.summonsLamps.length; i++) {
        const on = this.pinballT > 0.18 * i && this.pinballT < 0.18 * i + 0.9;
        if (this.summoned !== i + 1) this.summonsLamps[i].visible = on;
        if (on && this.pinballT - dt <= 0.18 * i) this.bus.emit('audio:flap');
      }
      if (this.pinballT > 2.0) {
        this.pinballT = -1;
        for (let i = 0; i < this.summonsLamps.length; i++) {
          if (this.summoned !== i + 1) this.summonsLamps[i].visible = false;
        }
      }
    }
  }
}
